/**
 * The RESELLER cell of an invoice / quote list row — the recipient reseller
 * (a portal quote request, or a customer document billed to its reseller).
 *
 * ONE component for BOTH tables that render `INVOICES_TABLE`: the shared
 * `document-list-table.tsx` (Quotes) and the inline table in
 * `pages/invoices/InvoicesPage.tsx`. The Reseller column joined the config on
 * 2026-09-15 and only the shared table grew the cell — the invoices page kept
 * writing its body rows one cell short, so every value from Amount onward sat
 * under the wrong header (the amount under "Reseller", the status under
 * "Amount", …) and v0.1.102 shipped that way. A header in the config with no
 * matching body cell is exactly the drift this file exists to make impossible:
 * add the column here, and both surfaces render it.
 *
 * Behind the reseller wall like every reseller name (`<ResellerOnly>`, with a
 * "—" fallback so the column keeps its width for a viewer without access).
 */
import React from "react";
import { Box, TableCell, Typography } from "@mui/material";

import { ResellerOnly } from "@crm-src/components/reseller";

export interface DocumentResellerCellProps {
  reseller?: { name: string } | null;
  /** A reseller's mirrored quote request (locked in the CRM). */
  locked?: boolean | undefined;
  onClick?: () => void;
}

const Dash = () => <Typography sx={{ fontSize: 14, color: "text.disabled" }}>—</Typography>;

export function DocumentResellerCell({ reseller, locked, onClick }: DocumentResellerCellProps): React.ReactElement {
  return (
    <TableCell
      sx={{ cursor: onClick ? "pointer" : undefined, maxWidth: 180 }}
      {...(onClick ? { onClick } : {})}
      data-cy="document-reseller"
    >
      <ResellerOnly fallback={<Dash />}>
        {reseller ? (
          <Box sx={{ maxWidth: 180 }}>
            <Typography
              sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              title={reseller.name}
            >
              {reseller.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
              {locked ? "Requested on XOP Anytime" : "Bill to"}
            </Typography>
          </Box>
        ) : (
          <Dash />
        )}
      </ResellerOnly>
    </TableCell>
  );
}
